import type { Bundle, Resource, ResourceType, Subscription } from "@medplum/fhirtypes";
import { useSubscription } from "@medplum/react-hooks";
import { syncKey } from "@repo/api";
import { SUPPORTED_INTERACTION_URL } from "@repo/fhir-systems";
import { useCallback, useMemo } from "react";
import type { FhirCollectionUtils } from "../../fhir/base";
import { useCollection } from "../hooks";
import { createReconnectRefetch } from "./reconnect";
import { parseSubscriptionBundle } from "./utils";

const RECENTLY_DELETED_TTL_MS = 5000;

/** Row keys deleted recently, to prevent re-insertion race conditions */
const recentlyDeletedKeys = new Map<string, number>();

/** Mark a row key as recently deleted - exported for use by optimistic delete actions */
export function markDeleted(key: string): void {
  recentlyDeletedKeys.set(key, Date.now());
}

function wasRecentlyDeleted(key: string): boolean {
  const deletedAt = recentlyDeletedKeys.get(key);
  if (!deletedAt) return false;
  if (Date.now() - deletedAt > RECENTLY_DELETED_TTL_MS) {
    recentlyDeletedKeys.delete(key);
    return false;
  }
  return true;
}

/** Medplum extension URL for supported subscription interactions */

/**
 * Delete-only subscription props.
 *
 * NOTE: two subscriptions per resource type is deliberate, and costs us against
 * the per-user WebSocket subscription cap (`maxUserWebSocketSubscriptions`,
 * default 20) — so it is worth knowing why collapsing them does not work.
 *
 * A notification carries no interaction marker: Medplum builds every one with
 * the same `SubscriptionStatus` + full resource, whether the resource was
 * created, updated or deleted (server `createSubEventNotification`, v5.1.11).
 * The delivering subscription is therefore the ONLY delete signal, which is why
 * `handleDeleteBundle` trusts its channel rather than the bundle. A single
 * subscription declaring no interaction extension does receive deletes — an
 * absent extension means all interactions — but they arrive indistinguishable
 * from updates, and get applied as upserts. Verified against a local 5.1.11
 * server: deleting a resource left the row on screen.
 *
 * `parseSubscriptionBundle`'s "delete" heuristic (missing or stub resource)
 * never fires on this server version. Do not rely on it here.
 */
const DELETE_ONLY_PROPS: Partial<Subscription> = {
  extension: [{ url: SUPPORTED_INTERACTION_URL, valueCode: "delete" }],
};

/**
 * Creates a subscription hook for a FHIR resource type.
 * Handles real-time sync via WebSocket subscriptions.
 *
 * @param resourceType - FHIR resource type to subscribe to
 * @param collectionName - Name of the collection in the registry
 */
export function createSubscriptionHook<T extends Resource>(resourceType: ResourceType, collectionName: string) {
  return function useResourceSubscription(enabled = true): void {
    const collection = useCollection<T, string, FhirCollectionUtils<T>>(collectionName);

    /**
     * Handle bundles from the default subscription (create + update events).
     * Due to Medplum bug, this may also receive delete events, so we check recentlyDeleted.
     */
    const handleDefaultBundle = useCallback(
      (bundle: Bundle) => {
        const event = parseSubscriptionBundle<T>(bundle);
        if (!event) return;

        const { resource } = event;

        // Skip if this record was recently deleted (prevents re-insertion race)
        if (wasRecentlyDeleted(syncKey(resource))) return;

        try {
          collection.utils.updateRecord(resource);
        } catch (error) {
          console.error(`[Subscription:${resourceType}] Error updating collection:`, error);
        }
      },
      [collection],
    );

    /**
     * Handle bundles from the delete subscription.
     * Trust that this subscription only fires for delete events (per extension filter).
     */
    const handleDeleteBundle = useCallback(
      (bundle: Bundle) => {
        const event = parseSubscriptionBundle<T>(bundle);
        if (!event) return;

        try {
          collection.utils.deleteRecord(event.resource);
        } catch (error) {
          console.error(`[Subscription:${resourceType}:delete] Error deleting:`, error);
        }
      },
      [collection],
    );

    const handleReconnect = useMemo(
      () =>
        createReconnectRefetch(
          () => collection.utils.refetch(),
          (error) => console.error(`[Subscription:${resourceType}] Refetch error on reconnect:`, error),
        ),
      [collection],
    );

    // Subscription 1: Create + Update (default behavior - no extensions needed)
    useSubscription(enabled ? resourceType : undefined, handleDefaultBundle, {
      onSubscriptionConnect: handleReconnect,
      onError: (error) => console.error(`[Subscription:${resourceType}] Error:`, error),
    });

    // Subscription 2: Delete only (explicit extension required)
    useSubscription(enabled ? resourceType : undefined, handleDeleteBundle, {
      subscriptionProps: DELETE_ONLY_PROPS,
      onSubscriptionConnect: handleReconnect,
      onError: (error) => console.error(`[Subscription:${resourceType}:delete] Error:`, error),
    });
  };
}
